import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Game,
  PopularGame,
  PopularGameList,
} from "@nestcoinco/hyper-api-gateway-api-client";

import "./GamesFeeds.scss";
import SomeoneWantsToPlayYouModal from "../../modals/SomeoneWantsToPlayYouModal/SomeoneWantsToPlayYouModal";
import DesktopShareTrayModal from "../../modals/DesktopShareTrayModal/DesktopShareTrayModal";
import useDisclosure from "../../../hooks/useDisclosure";
import useFavoriteGameMutation from "../../../react-query/mutations/use-favorite-game";
import {
  useGamesPageActions,
  useGamesPageScroll,
  useShareGameLinkAction,
} from "../../../hooks/useGamesPage";
import useAuth from "../../../hooks/useAuth";
import { REFERRER_QUERY_NAME, ROUTES } from "../../../utils/constants";
import {
  FunctionWithVoidReturn,
  ThemeVariant,
} from "../../../utils/constants/generic-types";
import Skeleton from "../../Skeleton/Skeleton";
import GameFeed from "../GameFeed/GameFeed";
import GameScrollGuideIndicator, {
  GameScrollGuideMessages,
} from "../GameScrollGuideIndicator/GameScrollGuideIndicator";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import {
  getStoredHasShownSwipeForMore,
  setStoredHasShownSwipeForMore,
} from "../../../utils/storage";
import PlayOverlay from "../PlayOverlay/PlayOverlay";

interface GamesFeedsProps {
  initialGame?: Game;
  gamesList: PopularGame[];
  gamesListQuery: UseInfiniteQueryResult<InfiniteData<PopularGameList>>;
  maxPages: number;
  openDownloadAppPrompt: FunctionWithVoidReturn;
}

enum PageFetchDirection {
  PREVIOUS,
  NEXT,
}

const INITIAL_GAME_INDEX = -1;

const generatePlayGameRoute = (slug: string) => {
  return `${ROUTES.GAMES.ROOT}/${slug}/play`;
};

export default function GamesFeeds({
  initialGame,
  gamesList,
  gamesListQuery,
  maxPages,
  openDownloadAppPrompt,
}: GamesFeedsProps) {
  const { slug } = useParams();
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [queryParams] = useSearchParams();
  const shortCode = queryParams.get("code");
  const referrerId = queryParams.get(REFERRER_QUERY_NAME);
  const challengeId = queryParams.get("challengeInvitationId");
  const [videoIsPlaying, setVideoIsPlaying] = useState<boolean>(true);

  const pageFetched = useRef<PageFetchDirection>();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const visibleGameVideoRef = useRef<HTMLVideoElement | null>(null);
  const assignVisibleGameVideoRef = (video: HTMLVideoElement | null) => {
    if (!video) return;

    video.muted = isMuted;
    // There's a quirky behaviour where sometimes the video's pause status would still be false, i.e. the video is playing, even when we've paused it.
    // Hence, we want to ensure that the video's pause status is aligned with our in-memory state.
    // So, if the video is reported as playing but our in-memory state specifies that it's not, we want to pause the video again.
    if (!video.paused && !videoIsPlaying) video.pause();

    if (video !== visibleGameVideoRef.current) {
      setVideoIsPlaying(true);
      const tryToPlayVideo = async () => {
        try {
          await video.play();
        } catch (err) {
          // Fallback to muted play
          video.muted = true;
          toggleMute(true);
          video.play().catch(() => setVideoIsPlaying(false));
        }
      };
      tryToPlayVideo();
      visibleGameVideoRef.current = video;
    }
  };

  const [gameScrollGuideMessage, setGameScrollGuideMessage] =
    useState<GameScrollGuideMessages>();
  const { visibleGameIndex, setVisibleGameIndex } = useGamesPageScroll({
    scrollContainerRef,
    defaultVisibleGameIndex: !!slug ? INITIAL_GAME_INDEX : undefined,
  });
  const [visibleGameIsFavorite, setVisibleGameIsFavorite] = useState<boolean>();
  const [visibleGameVideoIsLoading, setVisibleGameVideoIsLoading] =
    useState<boolean>(true);

  const filteredGamesList = useMemo(() => {
    if (!initialGame) return gamesList;
    return gamesList.filter(({ game }) => game.id !== initialGame?.id);
  }, [gamesList, initialGame]);

  const visibleGame = useMemo(() => {
    if (visibleGameIndex === INITIAL_GAME_INDEX) return initialGame;
    return filteredGamesList.length
      ? filteredGamesList[visibleGameIndex].game
      : undefined;
  }, [filteredGamesList, initialGame, visibleGameIndex]);

  const firstGameIsVisible = useMemo(() => {
    if (slug?.trim()) return visibleGameIndex === INITIAL_GAME_INDEX;
    if (gamesList.length) {
      return visibleGameIndex === 0 && !gamesListQuery.hasPreviousPage;
    }
  }, [
    gamesList.length,
    gamesListQuery.hasPreviousPage,
    slug,
    visibleGameIndex,
  ]);

  const lastGameIsVisible = useMemo(() => {
    return (
      !gamesListQuery.isLoading &&
      visibleGameIndex === gamesList.length - 1 &&
      !gamesListQuery.hasNextPage
    );
  }, [
    gamesList.length,
    gamesListQuery.hasNextPage,
    gamesListQuery.isLoading,
    visibleGameIndex,
  ]);

  const playGameRoute = useMemo(
    () => generatePlayGameRoute(visibleGame?.slug ?? ""),
    [visibleGame?.slug],
  );

  const {
    isOpen: isDesktopShareOpen,
    onOpen: openDesktopShare,
    onClose: closeDesktopShare,
  } = useDisclosure();
  const {
    isOpen: isSomeoneWantsToPlayYouModalOpen,
    onOpen: onOpenSomeoneWantsToPlayYouModal,
    onClose: onCloseSomeoneWantsToPlayYouModal,
  } = useDisclosure();

  const { addGameToFavorites, removeGameFromFavorites } =
    useFavoriteGameMutation();
  const { isDesktop, isCreateShareLinkPending, gameShareLink, handleShare } =
    useShareGameLinkAction({
      gameId: visibleGame?.id,
      desktopShareHandler: openDesktopShare,
    });
  const {
    isMuted,
    handleGenreClick,
    handlePlayTournament,
    handlePlayWithFriends,
    toggleMute,
    goToPlayGame,
  } = useGamesPageActions({
    openDownloadAppPrompt,
    playGameRoute,
    visibleVideoFeed: visibleGameVideoRef.current,
  });

  // This helps us to immediately show the updated favorite status of the visible game (when a user likes/unlikes) before the fresh data from the backend is received.
  const isGameMyFavorite = (game: Game) => {
    if (game.id === visibleGame?.id && visibleGameIsFavorite !== undefined) {
      return visibleGameIsFavorite;
    }
    return game.isUserFavorite;
  };

  const handleFavoriteGame = (game: Game) => {
    if (!isLoggedIn) {
      navigate(ROUTES.ROOT.LOG_IN);
      return;
    }

    if (isGameMyFavorite(game)) {
      setVisibleGameIsFavorite(false);
      removeGameFromFavorites(game.id ?? "");
    } else {
      setVisibleGameIsFavorite(true);
      addGameToFavorites(game.id ?? "");
    }
  };

  const triggerScrollGuide = useCallback(
    (message?: GameScrollGuideMessages) => {
      if (message === GameScrollGuideMessages.END) {
        setGameScrollGuideMessage(message);
        return;
      }

      const guideHasShownBefore = getStoredHasShownSwipeForMore();
      if (!firstGameIsVisible || guideHasShownBefore) return;

      setStoredHasShownSwipeForMore(true);
      setGameScrollGuideMessage(GameScrollGuideMessages.SWIPE_UP);
    },
    [firstGameIsVisible],
  );

  useEffect(() => {
    if (!shortCode) return;
    onOpenSomeoneWantsToPlayYouModal();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortCode]);

  useEffect(() => {
    if (lastGameIsVisible) triggerScrollGuide(GameScrollGuideMessages.END);
  }, [lastGameIsVisible, triggerScrollGuide]);

  // Hide scroll guide indicators 5 seconds after they show up
  useEffect(() => {
    let hideIndicatorTimeoutId: NodeJS.Timeout;
    const hideScrollIndicator = () => {
      hideIndicatorTimeoutId = setTimeout(
        () => setGameScrollGuideMessage(undefined),
        5000,
      );
    };

    if (gameScrollGuideMessage) hideScrollIndicator();
    return () => clearTimeout(hideIndicatorTimeoutId);
  }, [gameScrollGuideMessage]);

  // This updates the favorite status of a game when scrolled into view.
  // It helps to prevent a situation where even when a new game is scolled into view, the user-modified favorite status of the previously visible game is retained.
  // You can check the logic in the isGameMyFavorite and handleFavoriteGame functions defined above to understand why this situation is possible.
  useEffect(() => {
    if (!visibleGame) return;
    setVisibleGameIsFavorite(visibleGame.isUserFavorite);
  }, [visibleGame]);

  // Add a query param to the route as user scrolls
  useEffect(() => {
    if (!visibleGame || !slug) return;
    if (slug === visibleGame.slug) {
      navigate(pathname);
      return;
    }
    navigate(`${pathname}?g=${visibleGame.slug}`);
  }, [navigate, slug, pathname, visibleGame]);

  // Automatically unmute the video when it is played
  useEffect(() => {
    const visibleGameVideo = visibleGameVideoRef.current;
    if (!visibleGameVideo) return;

    const handlePlayStarted = () => {
      toggleMute(false);
      setVideoIsPlaying(true);
    };
    visibleGameVideo.addEventListener("play", handlePlayStarted);

    return () => {
      visibleGameVideo.removeEventListener("play", handlePlayStarted);
    };
  }, [toggleMute]);

  const updateVisibleGameIndex = useCallback(() => {
    // The multiplier is used to determine if we should add to or subtract from the current visible game index to determine the new game index.
    const multiplier =
      pageFetched.current === PageFetchDirection.PREVIOUS ? 1 : -1;
    const sizeOfFirstPage = gamesListQuery.data?.pages[0].size ?? 0;

    setVisibleGameIndex(
      (prevIndex) => prevIndex + sizeOfFirstPage * multiplier,
    );
  }, [gamesListQuery.data?.pages, setVisibleGameIndex]);

  // Fetch the next page when the user scrolls to the preantepenultimate (fourth-last) item in the game list
  useEffect(() => {
    if (
      visibleGameIndex >= gamesList.length - 4 &&
      gamesListQuery.hasNextPage &&
      !gamesListQuery.isFetchingNextPage
    ) {
      pageFetched.current = PageFetchDirection.NEXT;
      gamesListQuery.fetchNextPage();
      if (gamesListQuery.data?.pages.length === maxPages) {
        updateVisibleGameIndex();
      }
    }
  }, [
    visibleGameIndex,
    gamesList.length,
    gamesListQuery,
    maxPages,
    updateVisibleGameIndex,
  ]);

  // Fetch the previous page when the user scrolls back up to the fourth item in the game list
  useEffect(() => {
    if (
      visibleGameIndex <= 3 &&
      gamesListQuery.hasPreviousPage &&
      !gamesListQuery.isFetchingPreviousPage
    ) {
      pageFetched.current = PageFetchDirection.PREVIOUS;
      gamesListQuery.fetchPreviousPage();
      if (gamesListQuery.data?.pages.length === maxPages) {
        updateVisibleGameIndex();
      }
    }
  }, [visibleGameIndex, gamesListQuery, maxPages, updateVisibleGameIndex]);

  useEffect(() => {
    return () => {
      // Reset the visible game index when the component unmounts
      setVisibleGameIndex(0);
    };
  }, [setVisibleGameIndex]);

  return (
    <>
      <div className="scrollbar-hidden GamesFeeds" ref={scrollContainerRef}>
        <Skeleton
          width="100%"
          height="100%"
          isLoading={!visibleGame || visibleGameVideoIsLoading}
          theme={ThemeVariant.DARK}
          borderRadius={0}
          style={{ position: "absolute", top: 0, left: 0, zIndex: 1 }}
          shimmerStyle={{
            zIndex: 1,
            top: -25,
            rotate: "20deg",
            height: "102.5%",
          }}
        />
        {!videoIsPlaying && !!visibleGameVideoRef.current && <PlayOverlay />}
        {initialGame && (
          <GameFeed
            key={initialGame.id}
            ref={
              visibleGameIndex === INITIAL_GAME_INDEX
                ? assignVisibleGameVideoRef
                : undefined
            }
            game={initialGame}
            visibleVideo={visibleGameVideoRef.current}
            isVisibleGame={visibleGameIndex === INITIAL_GAME_INDEX}
            videoIsMuted={isMuted}
            gameIsFavorited={isGameMyFavorite(initialGame)}
            isCreateShareLinkPending={isCreateShareLinkPending}
            isVideoPlaying={videoIsPlaying}
            handlePlayGame={goToPlayGame}
            handleGenreClick={handleGenreClick}
            handlePlayTournament={handlePlayTournament}
            handleFavoriteGame={handleFavoriteGame}
            handlePlayWithFriends={handlePlayWithFriends}
            handleShare={handleShare}
            toggleMute={toggleMute}
            handleShowScrollIndicator={triggerScrollGuide}
            setVideoIsLoading={setVisibleGameVideoIsLoading}
            setVideoIsPlaying={setVideoIsPlaying}
          />
        )}
        {filteredGamesList.map(({ game }, index) => (
          <GameFeed
            key={game.id}
            ref={
              index === visibleGameIndex ? assignVisibleGameVideoRef : undefined
            }
            game={game}
            visibleVideo={visibleGameVideoRef.current}
            isVisibleGame={index === visibleGameIndex}
            videoIsMuted={isMuted}
            gameIsFavorited={isGameMyFavorite(game)}
            isCreateShareLinkPending={isCreateShareLinkPending}
            isVideoPlaying={videoIsPlaying}
            handlePlayGame={goToPlayGame}
            handleGenreClick={handleGenreClick}
            handlePlayTournament={handlePlayTournament}
            handleFavoriteGame={handleFavoriteGame}
            handlePlayWithFriends={handlePlayWithFriends}
            handleShare={handleShare}
            toggleMute={toggleMute}
            handleShowScrollIndicator={triggerScrollGuide}
            setVideoIsLoading={setVisibleGameVideoIsLoading}
            setVideoIsPlaying={setVideoIsPlaying}
          />
        ))}
        {gameScrollGuideMessage && (
          <GameScrollGuideIndicator message={gameScrollGuideMessage} />
        )}
      </div>
      <SomeoneWantsToPlayYouModal
        isOpen={isSomeoneWantsToPlayYouModalOpen}
        onClose={onCloseSomeoneWantsToPlayYouModal}
        shortCode={shortCode || ""}
        challengeId={challengeId || undefined}
        referrerId={referrerId || undefined}
      />
      {gameShareLink?.link && isDesktop && isDesktopShareOpen && (
        <DesktopShareTrayModal
          isOpen={isDesktopShareOpen}
          onClose={closeDesktopShare}
          link={gameShareLink.link}
          game={visibleGame}
        />
      )}
    </>
  );
}
